import { memo, useCallback } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';

import { BUILDER_MODE_V1_LAYOUT, BUILDER_MODE_V1_TOKENS } from '../../constants';

export interface FormatOption {
  type: string;
  label: string;
  value: string;
}

export interface FormatToggleGroupProperties {
  options: FormatOption[];
  activeFormat: string;
  onChange: (value: string) => void;
}

const FormatToggleGroup = memo(
  ({ options, activeFormat, onChange }: FormatToggleGroupProperties) => {
    const handleSelect = useCallback(
      (value: string) => {
        onChange(value);
      },
      [onChange],
    );

    return (
      <Flex gap={BUILDER_MODE_V1_LAYOUT.formulaControlsGap} align='center' flexShrink={0}>
        {options.map((item) => {
          const isActive = activeFormat === item.type;

          return (
            <Box
              key={item.value}
              as='button'
              type='button'
              aria-label={`Format as ${item.type}`}
              aria-pressed={isActive}
              onClick={() => handleSelect(item.value)}
              w={BUILDER_MODE_V1_LAYOUT.formatToggleWidth}
              minW={BUILDER_MODE_V1_LAYOUT.formatToggleWidth}
              h={BUILDER_MODE_V1_LAYOUT.formatToggleHeight}
              minH={BUILDER_MODE_V1_LAYOUT.formatToggleHeight}
              px='8px'
              py='5px'
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              borderRadius={BUILDER_MODE_V1_LAYOUT.actionButtonRadius}
              border='1px solid'
              borderColor={
                isActive ? BUILDER_MODE_V1_TOKENS.textDark : BUILDER_MODE_V1_TOKENS.placeholder
              }
              bg={isActive ? BUILDER_MODE_V1_TOKENS.textDark : BUILDER_MODE_V1_TOKENS.white}
              cursor='pointer'
              flexShrink={0}
              _hover={{
                bg: isActive ? BUILDER_MODE_V1_TOKENS.textDark : BUILDER_MODE_V1_TOKENS.activeBg,
              }}
            >
              <Text
                w='100%'
                textAlign='center'
                fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                fontSize='10px'
                fontWeight='400'
                lineHeight='normal'
                color={
                  isActive ? BUILDER_MODE_V1_TOKENS.activeBg : BUILDER_MODE_V1_TOKENS.textSecondary
                }
                noOfLines={1}
              >
                {item.type}
              </Text>
              <Text
                w='100%'
                textAlign='center'
                fontFamily={BUILDER_MODE_V1_LAYOUT.toolbarFontFamily}
                fontSize='15px'
                fontWeight='400'
                lineHeight='normal'
                color={isActive ? BUILDER_MODE_V1_TOKENS.white : BUILDER_MODE_V1_TOKENS.textDark}
                noOfLines={1}
              >
                {item.label}
              </Text>
            </Box>
          );
        })}
      </Flex>
    );
  },
);

FormatToggleGroup.displayName = 'FormatToggleGroup';

export default FormatToggleGroup;
